import { useCallback } from "react";
import { formatMessage, type MessageValues } from "../../i18n/catalogUtils";
import type { InstallableLocale } from "../../i18n/preparedLocales";
import { useWorkspaceCopy } from "../../i18n/workspaceCatalog";

const en = {
  "add.verification.autoChecking": "Checking this package automatically…",
  "add.verification.autoReady": "Preparing package verification…",
  "add.verification.autoFailed": "Package verification could not finish. No release was published.",
  "add.verification.autoRetry": "Retry verification",
  "add.verification.autoWaiting": "Windows verification is running. Results update automatically.",
  "add.verification.pollPaused": "Verification is still pending. Check again to continue following its result.",
  "add.upload.progress": "Artifact upload progress",
  "add.upload.confirming": "File sent — confirming server receipt",
  "add.upload.authorizing": "Waiting for authentication",
  "add.upload.sent": "{sent} sent",
  "add.upload.bytes": "{sent} of {total} sent · {percent}%",
  "add.upload.receiptPending": "Transfer is complete. Waiting for the server to stage and confirm the uploaded file.",

  "setup.pausedConfirmed": "Paused. Verified checks are saved. Recheck the plan before continuing.",
  "setup.actions": "Publishing actions",
  "readiness.otherActions": "Additional readiness actions",
  "setup.progress": "{completed} of {total} checks verified",
  "setup.progressLabel": "Publishing preparation progress",
  "setup.manualOptional": "Manual policy editing is optional. Setup can use the saved policy or the declared first-time defaults.",
  "detail.publication": "Publication",
  "detail.moreActions": "Release actions",
  "detail.recordDetails": "Release details",
  "readiness.nextStep": "Continue through the existing device setup or update flow. Publication and rollout settings are unchanged.",
  "readiness.issue.tufHealthInline": "Update trust cannot renew automatically. Review the checks here; required security review still applies.",
  "readiness.issue.tufRootInline": "No usable update Root authority is available. Review the preparation checks here; existing authority is preserved.",
  "readiness.issue.tufSigningInline": "The managed signing authority is incomplete or unavailable. Review the preparation checks here; separate review may be required.",
  "readiness.issue.tufConfigurationInline": "This deployment is missing required update-trust configuration. Review the diagnostic below; deployment repair may be required.",
  "readiness.issue.tufPolicyInline": "No maintenance policy is saved. The reviewed setup can use the declared first-time defaults; manual editing is optional.",

  "setup.title": "Secure Collector publishing",
  "setup.pendingRelease": "Update readiness pending",
  "setup.pause": "Pause setup",
  "setup.pausedBody": "Pausing after the current check. Completed work is saved; recheck before continuing.",
  "setup.scope": "Windows beta · One-time setup",
  "setup.body": "Approve the reviewed checks to prepare and verify publishing here.",
  "setup.signer": "Collector signing authority",
  "setup.policy": "Update trust validity policy",
  "setup.keys": "Update signing keys",
  "setup.root": "Verified public trust chain",
  "setup.activation": "Signing authority activated and checked",
  "setup.verified": "Verified",
  "setup.pending": "Will be prepared",
  "setup.loading": "Checking existing trust and server readiness…",
  "setup.working": "Preparing and verifying secure publishing…",
  "setup.approve": "Authorize secure setup",
  "setup.approval": "Changes require your approval and recent MFA.",
  "setup.controls": "Policy and advanced details",
  "setup.policyBody": "Existing policy is preserved. First-time defaults: Root 365 days, Targets 90, Snapshot 30, Timestamp 14, warning 7 days. Renewal remains a governed operation.",
  "setup.preserve": "Existing keys are reused. Root replacement and inconsistent authority require separate review. Every change is recorded in Audit log.",
  "setup.ready": "Secure publishing is ready",
  "setup.complete": "Approved work completed and verified.",
  "setup.paused": "Your attention is needed",
  "setup.retryBody": "This check could not finish. Completed steps are saved. Recheck before continuing; the release is not confirmed update-ready.",
  "setup.configuration": "This server is missing required publishing configuration. No replacement keys will be created. The deployment configuration needs repair.",
  "setup.changed": "The reviewed setup changed. Recheck the current plan and approve it again.",
  "setup.otherOperation": "Another setup operation is running. Recheck to see its result.",
  "setup.reviewRequired": "Existing trust needs review. Kavoosh preserved the current keys and will not replace them automatically. Expand technical details for the diagnostic code.",
  "setup.finish": "Complete update readiness",
  "setup.existing": "Existing release · Registration, publication and rollout are preserved.",
  "setup.publish": "Authorize setup and publish beta",
  "setup.intakeScope": "This package · Windows beta · 100% of eligible beta devices",
  "setup.registrationNote": "Registration does not publish this package. Secure publishing can be prepared here without leaving the wizard.",
  "hub.eyebrow": "PLATFORM DELIVERY",
  "hub.title": "Collector Releases",
  "hub.description": "Add, verify, register, publish and manage Collector artifacts.",
  "tab.registry": "Collector Releases",
  "tab.consent": "Consent",
  "tab.consents": "Consent",
  "tab.add": "Add Collector",
  "workspace.aria": "Collector Release workspace",
  "registry.aria": "Collector Release registry",
  "registry.eyebrow": "COLLECTOR RELEASE REGISTRY",
  "registry.title": "Collector Releases",
  "registry.description": "Review every current Collector authority, including LAB-published portable builds. Published records are withdrawn rather than silently deleted so release history remains auditable.",
  "action.refresh": "Refresh",
  "action.refreshing": "Refreshing…",
  "action.add": "Add Collector",
  "search.aria": "Search Collector Releases",
  "search.placeholder": "Search version, platform, channel, hash…",
  "filter.sourceAria": "Filter Collector Release source",
  "filter.lifecycleAria": "Filter Collector Release lifecycle",
  "filter.sourceAll": "All sources",
  "filter.labPortable": "Portable · LAB",
  "filter.signedManifest": "Signed manifest",
  "filter.signedBundle": "Signed bundle",
  "filter.directArtifact": "Direct artifact",
  "filter.stateAll": "All states",
  "state.published": "Published",
  "state.registered": "Registered",
  "state.verified": "Verified",
  "state.revoked": "Revoked",
  "state.unknown": "Other state",
  "readiness.ready": "Ready",
  "readiness.attention": "Attention required",
  "readiness.blocked": "Blocked",
  "readiness.readyBody": "This release is internally consistent and the required update trust is aligned.",
  "readiness.updateTrust": "Update readiness",
  "readiness.tufExact": "TUF authorizes these exact Collector bytes.",
  "readiness.tufNotExact": "TUF does not yet authorize these exact Collector bytes.",
  "readiness.repair": "Make update-ready",
  "readiness.repairing": "Repairing…",
  "readiness.recheck": "Recheck readiness",
  "readiness.renewTrust": "Renew update trust",
  "readiness.renewingTrust": "Renewing trust…",
  "readiness.renewedReady": "Update trust renewed at metadata generation v{version}; this Collector is ready.",
  "readiness.renewedRepairNext": "Update trust renewed at metadata generation v{version}. The trust is healthy again; use Make update-ready to authorize these exact Collector bytes.",
  "readiness.renewedRecheck": "Update trust renewed at metadata generation v{version}. Recheck the remaining release authority before continuing.",
  "readiness.renewError": "Update trust could not be renewed safely. Review the reported trust authority and try again only after it is resolved.",
  "readiness.managePolicy": "Update trust policy",
  "readiness.policyRequired": "Configure the update trust maintenance policy here before renewal. No trust metadata was changed.",
  "readiness.openKeysTrust": "Open Keys & Trust",
  "readiness.openConsent": "Open Consent",
  "readiness.addSuccessor": "Add successor Collector",
  "readiness.repaired": "Collector Release readiness is now aligned.",
  "readiness.recheckRequired": "Kavoosh completed the safe repair. Recheck readiness before continuing.",
  "readiness.error": "Collector Release readiness could not be checked or repaired.",
  "readiness.issue.releaseNotPublished": "This Collector is not published yet. Complete the release publication step before using it for updates.",
  "readiness.issue.artifactIncomplete": "This published Release no longer has a complete immutable artifact binding. It cannot be rewritten in place. Withdraw it, then add a fresh successor Collector version with the exact finished artifact; do not reuse this release identity.",
  "readiness.issue.consentIncomplete": "This published Release is missing its exact governed Consent Set and cannot be rewritten. Complete the current Consent policy, then add a fresh successor Collector version and bind that exact Consent Set.",
  "readiness.issue.tufMismatch": "This Collector is published, but Windows update trust still points to different bytes. Kavoosh can safely update TUF to this exact release.",
  "readiness.issue.tufRecovery": "A previous update-trust operation stopped after creating recoverable state for this exact release. Kavoosh can safely reconcile it.",
  "readiness.issue.tufRoot": "Windows update trust has no usable Root authority. Open Keys & Trust and restore or activate the required TUF Root authority.",
  "readiness.issue.tufRootExpired": "The TUF Root authority is expired. Mutable metadata renewal cannot repair Root; open Keys & Trust and follow the Root recovery or rotation path before continuing.",
  "readiness.issue.tufExpired": "Windows update trust metadata is expired. Renew it here: Kavoosh preserves the exact current Collector target, verifies Root and managed signers, and commits Timestamp last.",
  "readiness.issue.tufExpiredUnbound": "Windows update trust expired before any Collector target was authorized. Kavoosh can safely bind this exact Release while creating a fresh metadata generation; Root and managed signers are reverified and Timestamp commits last.",
  "readiness.issue.tufPolicy": "Update trust maintenance policy is not configured. Configure it here, then recheck this release.",
  "readiness.issue.tufHealth": "Windows update trust is unhealthy for a reason that is not safe to auto-renew. Open Keys & Trust and follow the reported repair path.",
  "readiness.issue.tufSigning": "The managed TUF signing authority is incomplete or unavailable. Open Keys & Trust and resolve the signer shown there, then recheck.",
  "readiness.issue.tufConfiguration": "Managed Windows update trust is not fully configured for this deployment. Open Keys & Trust and complete the TUF setup shown there, then recheck this release.",
  "readiness.issue.tufRecoveryBlocked": "Retained TUF recovery state is invalid and cannot be repaired automatically. Open Keys & Trust and follow the recovery guidance.",
  "readiness.issue.tufRecoveryOther": "A retained TUF recovery operation belongs to another release. Resolve that recovery in Keys & Trust before publishing a new update target.",
  "readiness.issue.releaseIncompatible": "This release cannot use the current managed update-trust path. Review the release authority before continuing.",
  "readiness.issue.generic": "Kavoosh detected an authority mismatch. Recheck readiness or follow the guided remediation before continuing.",
  "history.show": "Show record history",
  "metrics.aria": "Collector Release metrics",
  "intakes.aria": "Open Collector artifact intakes",
  "intakes.eyebrow": "WORK IN PROGRESS",
  "intakes.title": "Open artifact intakes",
  "intakes.emptyTitle": "No open artifact intakes",
  "intakes.emptyBody": "There are no unfinished Collector artifact workflows. Start Add Collector when you are ready to register another artifact.",
  "intakes.count": "{count} open",
  "intakes.identityPending": "Identity pending",
  "intakes.unknownPlatform": "Unknown platform",
  "intakes.consentBound": "Consent bound",
  "intakes.consentPending": "Consent pending",
  "intakes.state.stagingPending": "Staging pending",
  "intakes.state.notStarted": "Not started",
  "intakes.state.nativePending": "Native verification pending",
  "intakes.state.nativeVerified": "Native verified",
  "intakes.state.labPortableVerified": "LAB portable verified",
  "intakes.state.failed": "Failed",
  "intakes.state.discardPending": "Discard pending",
  "intakes.state.discarded": "Discarded",
  "intakes.openWorkflow": "Open workflow",
  "intakes.discard": "Discard",
  "intakes.discardBlocked": "Wait for native verification to finish before discarding this intake.",
  "empty.title": "No Collector Releases match this view",
  "empty.body": "Add a Collector or clear the current filters. LAB-published portable releases are included in this registry.",
  "directory.eyebrow": "RELEASE DIRECTORY",
  "directory.title": "Release directory",
  "directory.history": "Release record history",
  "directory.current": "Current releases",
  "directory.count": "{count} record{suffix}",
  "action.manage": "Manage",
  "detail.aria": "Collector Release details",
  "detail.eyebrow": "RELEASE AUTHORITY",
  "detail.close": "Close",
  "detail.overview": "Release",
  "detail.overviewHelp": "Authoritative release lifecycle and delivery state.",
  "detail.trust": "Trust & artifact",
  "detail.trustHelp": "Exact package, runtime, manifest and signing authority.",
  "detail.consentLifecycle": "Consent & lifecycle",
  "detail.consentLifecycleHelp": "Immutable Consent and audited release actions.",
  "detail.refreshHelp": "Reload server-owned Registry state.",
  "field.state": "State",
  "field.record": "Record",
  "field.packageSha": "Package SHA-256",
  "field.runtimeSha": "Runtime SHA-256",
  "field.artifactSize": "Artifact size",
  "field.channel": "Channel",
  "field.rollout": "Rollout",
  "field.distribution": "Distribution",
  "field.platformOrigin": "Platform origin",
  "field.manifestSha": "Manifest SHA-256",
  "field.signingKey": "Signing key",
  "field.nativeVerifier": "Native verifier",
  "field.consentSet": "Consent Set",
  "field.artifactUrl": "Artifact URL",
  "field.created": "Created",
  "field.published": "Published",
  "field.revoked": "Revoked",
  "field.notPublished": "Not published",
  "field.openArtifact": "Open exact artifact",
  "consent.title": "Consent authority",
  "consent.body": "The Consent Set retained by this release is immutable historical evidence. New Consent versions are managed separately and apply to future collection requests without rewriting this release record.",
  "consent.revise": "Manage Consent versions",
  "consent.libraryTitle": "Consent documents bound to this Release",
  "consent.libraryHelp": "These are the exact historical Consent documents retained by this Collector Release. Bound history is read-only; create, translate, publish or restore future Consent versions from Consent policies.",
  "consent.librarySearch": "Search Consent by profile, language or version",
  "consent.libraryLanguage": "Language",
  "consent.libraryAllLanguages": "All languages",
  "consent.libraryProfile": "Collection profile",
  "consent.libraryVersion": "Bound version",
  "consent.libraryRecord": "Content record",
  "consent.libraryPreview": "Preview bound",
  "consent.libraryAction": "Manage current",
  "consent.libraryPreviewUnavailable": "This historical Consent document is no longer available for exact preview.",
  "consent.libraryCount": "{count} document{suffix}",
  "consent.libraryEmpty": "This Release does not retain searchable Consent document detail. The historical Consent Set digest remains authoritative.",
  "consent.libraryNoMatch": "No bound Consent documents match this search.",
  "consent.libraryImmutable": "Release-bound Consent history cannot be deleted or edited. Draft and future Consent management happens in Consent policies.",
  "action.changeRollout": "Change rollout",
  "action.withdraw": "Withdraw release",
  "lab.rolloutNote": "LAB portable rollout is signed at 100% in the current contract and cannot be edited in place. Withdrawal is available; variable rollout will move to a separate rollout authority rather than mutating the signed package manifest.",
  "discard.aria": "Discard Collector artifact intake",
  "discard.eyebrow": "DISCARD INTAKE",
  "discard.body": "This removes the unfinalized quarantine artifact from active Admin work. It is allowed only while no Collector Release authority references this intake. Published or registered releases are never hard-deleted here.",
  "discard.cancel": "Cancel",
  "discard.confirm": "Discard intake",
  "discard.working": "Discarding…",
  "withdraw.aria": "Withdraw Collector Release",
  "withdraw.eyebrow": "WITHDRAW RELEASE",
  "withdraw.body": "Withdrawal creates a new audited revoked record and removes this release from future bootstrap selection. Historical release evidence is retained.",
  "withdraw.reason": "Reason",
  "withdraw.placeholder": "Explain why this release must no longer be offered.",
  "withdraw.cancel": "Cancel",
  "withdraw.confirm": "Withdraw release",
  "withdraw.working": "Applying…",
  "rollout.aria": "Change rollout",
  "rollout.eyebrow": "ROLLOUT CONTROL",
  "rollout.body": "Change the percentage of eligible Devices that may receive this published release.",
  "rollout.label": "Rollout percent",
  "rollout.cancel": "Cancel",
  "rollout.confirm": "Apply rollout",
  "rollout.working": "Applying…",
  "status.withdrawn": "{version} was withdrawn. New bootstrap selection will no longer use this release.",
  "status.rollout": "Rollout for {version} is now {percent}%.",
  "status.discarded": "{filename} was discarded. No Release authority was deleted.",
  "error.load": "Collector Releases could not be loaded.",
  "error.change": "Collector Release change failed.",
  "error.discard": "Collector artifact intake could not be discarded.",
  "source.labPortable": "Portable · LAB",
  "source.directArtifact": "Direct artifact",
  "source.signedBundle": "Signed bundle",
  "source.signedManifest": "Signed manifest",
  "distribution.portable": "Portable",
  "distribution.setup": "Setup",
  "channel.beta": "Beta",
  "channel.stable": "Stable",
  "metrics.current": "Current releases",
  "metrics.currentHelp": "Latest authority per release key",
  "metrics.published": "Published",
  "metrics.publishedHelp": "Eligible release records currently active",
  "metrics.intakes": "Open intakes",
  "metrics.intakesHelp": "Draft artifact work not yet bound to a Release",
  "filter.sourceLabel": "Source",
  "filter.stateLabel": "State",
  "filter.clear": "Clear filters",
  "column.collector": "Collector",
  "column.platform": "Platform",
  "column.distribution": "Distribution",
  "column.channel": "Channel",
  "column.state": "State",
  "column.updated": "Updated",
  "column.artifact": "Artifact",
  "column.version": "Version",
  "column.verification": "Verification",
  "column.consent": "Consent",
  "column.actions": "Actions",
  "directory.rollout": "{percent}% rollout",
  "directory.auditNote": "Published records remain auditable after withdrawal.",
  "intakes.separation": "Unpublished artifact work stays separate from the authoritative Release directory.",
  "consentUX.modeTabsAria": "Collector Consent modes",
  "intakes.state.publisherVerified": "Publisher signature verified",
  "field.publisherSignature": "Trusted publisher fingerprint",
  "add.verification.publisherVerify": "Verify publisher-signed ZIP",
  "add.verification.publisherPassed": "Publisher signature verified independently.",
  "add.verification.publisherNotNative": "Publisher signature verification is separate from Windows native code-signing verification.",
  "add.error.publisherVerificationFailed": "Publisher verification failed. Check Keys & Trust and the signed ZIP.",
  "add.consent.missingRequired": "Add required collection variables before saving: {variables}",
  "consentUX.identityReady": "Identity confirmed.",
  "consentUX.sharedEditor": "Consent is Admin-authored here and uses governed draft, exact preview and publication gates.",
  "consentUX.published": "Published",
  "consentUX.draft": "Draft",
  "consentUX.notPublished": "Not published",
  "consentUX.eyebrow": "COLLECTOR CONSENT",
  "consentUX.fallback": "Governed {mode} Consent for {platform}.",
  "consentUX.validatedDraft": "This exact draft passed preview and publication validation.",
  "consentUX.reused": "Reusing the current published Collector Consent version.",
  "consentUX.immutableDraft": "Immutable draft created",
  "consentUX.previewInstruction": "Preview the exact server-rendered Consent before publication.",
  "consentUX.loading": "Loading…",
  "consentUX.previewExact": "Preview exact Consent",
  "consentUX.exactPreview": "Exact preview",
  "consentUX.publishing": "Publishing…",
  "consentUX.publishExact": "Publish exact version",
  "consentUX.authorityTitle": "Windows Collector authority",
  "consentUX.profile": "Profile",
  "consentUX.generation": "Generation",
  "consentUX.authoritySha": "Authority SHA-256",
  "consentUX.artifactScopeSha": "Artifact scope SHA-256",
  "consentUX.boundSha": "Bound body SHA-256",
  "consentUX.bindHelp": "Bind only after Rapid Triage, Deep Forensics and Advanced Threat Hunt each have an exact published Consent version.",
  "consentManager.aria": "Collector Consent management",
  "consentManager.eyebrow": "COLLECTOR CONSENT",
  "consentManager.title": "Consent management",
  "consentManager.description": "Write, version, translate, preview, publish and restore the Consent shown before forensic collection. English is the authoritative source language.",
  "consentManager.platform": "Platform",
  "consentManager.profile": "Collection profile",
  "consentManager.language": "Language",
  "consentManager.englishDefault": "English is the built-in source and default. Other enabled languages are governed translations bound to an exact English version.",
  "consentManager.current": "Current version",
  "consentManager.notConfigured": "Not configured",
  "consentManager.newVersion": "Create new version",
  "consentManager.editTranslation": "Edit translation",
  "consentManager.preview": "Preview",
  "consentManager.publish": "Publish",
  "consentManager.effectiveAt": "Effective date and time",
  "consentManager.effectiveAtHelp": "Leave empty to publish immediately. Choose a future time to schedule the Consent cutover.",
  "consentManager.effectiveAtInvalid": "Choose a valid effective date and time.",
  "consentManager.sendReview": "Send to review",
  "consentManager.markReady": "Mark ready",
  "consentManager.history": "Version history",
  "consentManager.rollback": "Restore as new version",
  "consentManager.refresh": "Refresh",
  "consentManager.sourceRequired": "Publish the English source before creating translations.",
  "consentManager.translationSource": "Translation source",
  "consentManager.translationDraft": "Translation draft",
  "consentManager.translationTitle": "Translated title",
  "consentManager.translationBody": "Translated body",
  "consentManager.changeSummary": "Change summary",
  "consentManager.saveTranslation": "Save translation draft",
  "consentManager.cancel": "Cancel",
  "consentManager.readyHelp": "Preview the exact Ready translation before publication.",
  "consentManager.published": "Published",
  "consentManager.draft": "Draft",
  "consentManager.inReview": "In review",
  "consentManager.ready": "Ready",
  "consentManager.outOfDate": "Out of date",
  "consentManager.missing": "Missing",
  "consentManager.saved": "Consent draft saved.",
  "consentManager.translationSaved": "Translation draft saved.",
  "consentManager.publishedNotice": "Consent version published.",
  "consentManager.rolledBack": "Historical Consent restored as a new published version.",
  "consentManager.error": "Collector Consent could not be updated.",
  "consentManager.directoryTitle": "Consent policies",
  "consentManager.directoryDescription": "Choose a Collector platform and collection profile. Each policy keeps its own current version, language coverage, history and release bindings.",
  "consentManager.platformAria": "Collector platform",
  "consentManager.manageProfile": "Manage Consent",
  "consentManager.currentPublished": "Current published",
  "consentManager.noPublishedVersion": "No published version",
  "consentManager.versionCount": "{count} version{suffix}",
  "consentManager.languageCoverage": "Language coverage",
  "consentManager.languageCoverageValue": "{current} of {total} current",
  "consentManager.languageAttention": "{count} need attention",
  "consentManager.latestCollector": "Latest published Collector",
  "consentManager.noPublishedCollector": "No published Collector",
  "consentManager.releaseBindings": "Release bindings",
  "consentManager.releaseBindingCount": "{count} release{suffix}",
  "consentManager.backToOverview": "All Consent policies",
  "consentManager.currentForRequests": "Used for new {profile} requests",
  "consentManager.languagesTitle": "Languages",
  "consentManager.languagesHelp": "English is the source. Each translation is bound to one exact English source version and must be updated when that source changes.",
  "consentManager.sourceLanguage": "Source",
  "consentManager.chooseLanguage": "Manage this language",
  "consentManager.boundToSource": "Bound to EN {version}",
  "consentManager.releaseBindingsTitle": "Collector release bindings",
  "consentManager.releaseBindingsDescription": "Each Release keeps the exact Consent Set captured when it was registered or published. New Consent versions do not rewrite these historical bindings.",
  "consentManager.bindingLoadError": "Collector Release binding details could not be loaded. Consent authoring remains available.",
  "consentManager.bindingExact": "Bound Consent {version} · {count} language{suffix}",
  "consentManager.bindingDigestOnly": "Exact profile detail is retained by this Consent Set but is not available from this Release row.",
  "consentManager.currentState": "CURRENT",
  "consentManager.previousState": "PREVIOUSLY PUBLISHED",
  "consentManager.notBound": "No Release binding",
  "consentManager.releaseBindingState": "{state} · Consent Set {digest}",
  "add.mode.metadata": "Rapid Triage",
  "add.mode.extended": "Deep Forensics",
  "add.mode.memory": "Advanced Threat Hunt",
  "add.stage.artifact": "Artifact",
  "add.stage.identity": "Identity",
  "add.stage.consent": "Consent",
  "add.stage.verification": "Verification",
  "add.stage.review": "Review",
  "add.stage.done": "Done",
  "add.error.selectedNotResumable": "Selected Collector intake is not resumable.",
  "add.error.selectedResumeFailed": "Selected Collector intake could not be resumed.",
  "add.error.noResumable": "No resumable Collector intake is available.",
  "add.error.resumeFailed": "Collector intake could not be resumed.",
  "add.error.uploadFailed": "Artifact upload failed.",
  "add.error.identityRequired": "Operating system, architecture, version, and required host OS must be provided.",
  "add.error.identityFailed": "Identity confirmation failed.",
  "add.error.localeUnsupported": "Choose a supported prepared locale before saving this Consent draft.",
  "add.error.consentDraftFailed": "Consent draft failed.",
  "add.error.consentPreviewFailed": "Consent preview failed.",
  "add.error.contentValidationFailed": "Content validation did not approve this draft.",
  "add.error.consentPublicationFailed": "Consent publication failed.",
  "add.error.allConsentsRequired": "Publish all three exact Consent versions before binding.",
  "add.error.consentBindingFailed": "Consent binding failed.",
  "add.error.labVerificationFailed": "LAB portable integrity verification failed.",
  "add.error.labPublicationFailed": "LAB portable publication failed.",
  "add.error.nativeVerificationStartFailed": "Native verification could not start.",
  "add.error.verificationRefreshFailed": "Verification status could not be refreshed.",
  "add.error.finalAddFailed": "Final Add failed.",
  "add.consent.bindWorking": "Binding…",
  "add.consent.bindAction": "Bind three Consent versions",
  "add.verification.aria": "Collector verification",
  "add.verification.boundTitle": "Consent Set bound",
  "add.verification.targetOs": "Target OS",
  "add.verification.state": "Verification",
  "add.verification.starting": "Starting…",
  "add.verification.runNative": "Run native verification",
  "add.verification.checking": "Checking…",
  "add.verification.labTest": "Verify as LAB portable test",
  "add.verification.pending": "Windows WinVerifyTrust verification is pending.",
  "add.verification.refreshing": "Refreshing…",
  "add.verification.refresh": "Refresh verification status",
  "add.verification.failed": "Native verification failed: {reason}",
  "add.verification.labPassed": "LAB portable integrity verified.",
  "add.verification.verifier": "Verifier",
  "add.verification.package": "Package",
  "add.verification.runtimeSha": "Runtime SHA-256",
  "add.verification.runtimeSize": "Runtime size",
  "add.verification.authenticode": "Authenticode",
  "add.verification.trustScope": "Trust scope",
  "add.verification.publishLab": "Publish to LAB Beta",
  "add.verification.nativePassed": "Native verification passed.",
  "add.verification.policy": "Policy",
  "add.verification.verified": "Verified",
  "add.verification.reviewFinal": "Review Final Add",
  "add.review.aria": "Final Add review",
  "add.review.title": "Review exact Collector authority",
  "add.review.artifact": "Artifact",
  "add.review.artifactSize": "Artifact size",
  "add.review.artifactSha": "Artifact SHA-256",
  "add.review.architecture": "Architecture",
  "add.review.version": "Version",
  "add.review.consentSet": "Consent Set",
  "add.review.back": "Back",
  "add.review.adding": "Adding…",
  "add.review.finalAdd": "Final Add Collector",
  "add.success.labAria": "LAB Collector published",
  "add.success.labTitle": "LAB Collector published",
  "add.success.releaseRecord": "Release record",
  "add.success.manifestSha": "Manifest SHA-256",
  "add.success.signingKey": "Signing key",
  "add.success.channel": "Channel",
  "add.success.artifactUrl": "Artifact URL",
  "add.success.registeredAria": "Collector registered",
  "add.success.registeredTitle": "Collector registered",
  "add.success.reservation": "Reservation",
  "add.success.receiptSha": "Receipt SHA-256",
  "add.success.viewRegistered": "View Registered Collectors",
  "add.success.done": "Done",
  "add.intro.aria": "Add Collector",
  "add.intro.title": "Add Collector",
  "add.intro.body": "Upload, verify, review and register one final Collector artifact.",
  "add.workflow.aria": "Add Collector workflow",
  "add.workflow.eyebrow": "COLLECTOR ARTIFACT INTAKE",
  "add.workflow.title": "Add Collector",
  "add.workflow.body": "Add the package, confirm its details, and review before registration or publication.",
  "add.workflow.progressAria": "Add Collector progress",
  "add.step1.eyebrow": "STEP 2 OF 7",
  "add.step1.title": "Collector artifact",
  "add.step1.body": "Upload one final Collector executable, installer or ZIP package.",
  "add.step1.choose": "Choose artifact or ZIP",
  "add.step1.types": "EXE, MSI, package or ZIP",
  "add.step1.authorityHelp": "The selected bytes are hashed and become the artifact authority for this intake.",
  "add.step1.loading": "Loading…",
  "add.step1.resume": "Resume latest intake",
  "add.step1.uploading": "Uploading…",
  "add.step1.upload": "Upload artifact",
  "add.step2.eyebrow": "STEP 3 OF 7",
  "add.step2.title": "Confirm identity",
  "add.step2.body": "Review the server suggestion and correct it before creating durable identity authority.",
  "add.step2.filename": "Filename",
  "add.step2.size": "Size",
  "add.step2.sha": "SHA-256",
  "add.step2.suggestedOs": "Suggested operating system",
  "add.step2.noSuggestion": "No safe suggestion",
  "add.step2.select": "Select",
  "add.step2.help": "Detector output is only a suggestion. The Admin-confirmed identity becomes authoritative.",
  "add.step2.saving": "Saving…",
  "add.step2.confirm": "Confirm identity",
  "add.step3.eyebrow": "STEP 4 OF 7",
  "add.step3.title": "Consent",
  "add.step3.body": "Publish and bind one exact governed Consent version for each collection mode.",
  "add.step4.eyebrow": "STEP 5 OF 7",
  "add.step4.title": "Native verification",
  "add.step4.body": "Kavoosh verifies the exact artifact against the confirmed target platform.",
  "add.step5.eyebrow": "STEP 6 OF 7",
  "add.step5.title": "Review Collector",
  "add.step5.body": "Confirm the exact immutable authority that will be registered.",
  "add.action.open": "Add Collector",
  "add.action.cancel": "Cancel",
  "add.preview.draft": "Draft",
  "add.consent.previewAria": "{mode} exact preview",
  "add.verification.consentSetSha": "Consent Set SHA-256",
  "add.verification.unsupported": "Native verification adapter is not implemented for {platform}. Final Add remains fail-closed for this target.",
  "add.verification.labWarning": "LAB portable verification checks the exact ZIP and Windows runtime integrity only. It does not claim Authenticode trust and cannot unlock production Final Add.",
  "add.verification.labResultWarning": "This is a LAB-only integrity result. Production Final Add remains locked. LAB publication remains a separate signed authority.",
  "add.review.targetOs": "Target OS",
  "add.review.authorityHelp": "Final Add permanently reserves this component/version and promotes only these verified artifact bytes. It does not publish or roll out the Collector.",
  "add.success.labBody": "The exact portable ZIP is published to LAB Beta under a managed signed release authority. Production Final Add remains unchanged.",
  "add.success.labNext": "Next: open the Client forensic wizard for a Windows device, resolve this release, download the same ZIP, and start secure enrollment.",
  "add.success.registeredBody": "The exact verified artifact is registered. Nothing was automatically published or rolled out.",
  "add.field.operatingSystem": "Operating system",
  "add.field.architecture": "Architecture",
  "add.field.version": "Version",
  "add.field.component": "Component",
  "add.field.hostOperatingSystem": "Host operating system",
  "add.component.mobileHost": "Mobile host",
  "add.component.collector": "Collector",
  "add.stage.platform": "Platform & package",
  "add.platform.windows": "Windows",
  "add.platform.macos": "macOS",
  "add.platform.linux": "Linux",
  "add.platform.android": "Android",
  "add.platform.ios": "iOS",
  "add.stepPlatform.eyebrow": "STEP 1 OF 7",
  "add.stepPlatform.title": "Choose platform and package type",
  "add.stepPlatform.body": "Choose the Collector family and the package supplied by your Release Engineering team.",
  "add.stepPlatform.help": "Kavoosh shows only server-supported Collector capabilities. Package types are registered independently.",
  "add.distribution.portableHelp": "Runs without installation.",
  "add.distribution.setupHelp": "Installs on the target or host system.",
  "add.next": "Next",
  "add.back": "Back",
  "add.error.foundationLoad": "Collector Release capabilities or language settings could not be loaded.",
  "add.error.androidConsentUnavailable": "This platform server does not provide Android USB Consent support for all three profiles. Update the platform before previewing or publishing these Consents. Keep the saved drafts and the Android scope variable.",
  "add.error.hostRequired": "Choose the host operating system for this mobile Host Collector.",
  "add.consent.coverageReady": "All enabled languages ready",
  "add.consent.coverageNeedsAttention": "Language content needs attention",
  "add.consent.languagesAria": "Enabled Consent languages",
  "add.consent.activeLanguages": "{count} enabled language(s)",
  "add.consent.languagesFromPlatform": "This list comes from Platform language settings. Languages are not added from this wizard.",
  "add.consent.current": "Current",
  "add.consent.title": "Title",
  "add.consent.body": "Consent text",
  "add.consent.titlePlaceholder": "Enter a clear Consent title",
  "add.consent.bodyPlaceholder": "Write the Consent text for this language and profile",
  "add.consent.variables": "Available variables",
  "add.consent.variablesHelp": "These variables come from the governed Consent template for this profile.",
  "add.consent.required": "Required",
  "add.consent.saveDraft": "Save draft",
  "add.consent.allLanguagesReady": "All enabled languages are current for all three forensic profiles.",
  "add.consent.completeAllLanguages": "Complete and publish Consent for every enabled language before continuing.",
  "add.consent.languages": "Consent languages",
  "add.verification.body": "Kavoosh verifies the exact uploaded bytes. Technical verification details stay available without becoming Admin input.",
  "add.verification.labTools": "LAB testing tools",
  "add.technicalDetails": "Technical details",
  "add.success.readyForPublication": "Registered and ready for the publication boundary",
  "add.success.publicationBody": "Registration is complete. Publication and rollout remain a separate controlled action and are never implied by registration.",
} as const;

export type CollectorReleaseRegistryMessageKey = keyof typeof en;
export const COLLECTOR_RELEASE_REGISTRY_MESSAGE_KEYS = Object.keys(en) as CollectorReleaseRegistryMessageKey[];

type PartialCatalog = Partial<Record<CollectorReleaseRegistryMessageKey, string>>;

const fa: PartialCatalog = {
  "add.verification.autoChecking": "در حال بررسی خودکار این بسته…",
  "add.verification.autoReady": "در حال آماده‌سازی بررسی بسته…",
  "add.verification.autoFailed": "بررسی بسته کامل نشد. هیچ نسخه‌ای منتشر نشد.",
  "add.verification.autoRetry": "تلاش دوباره برای بررسی",
  "add.verification.autoWaiting": "بررسی ویندوز در حال اجراست. نتیجه خودکار به‌روز می‌شود.",
  "add.verification.pollPaused": "بررسی هنوز در انتظار است. برای پیگیری نتیجه دوباره بررسی کنید.",
  "add.upload.progress": "پیشرفت آپلود فایل",
  "add.upload.confirming": "فایل ارسال شد — در حال تأیید دریافت سرور",
  "add.upload.authorizing": "در انتظار تأیید هویت",
  "add.upload.sent": "{sent} ارسال شد",
  "add.upload.bytes": "{sent} از {total} ارسال شد · {percent}٪",
  "add.upload.receiptPending": "ارسال کامل شد. منتظر ذخیره‌سازی و تأیید فایل توسط سرور هستیم.",

  "setup.pausedConfirmed": "متوقف شد. بررسی‌های تأییدشده حفظ شده‌اند؛ پیش از ادامه، برنامه را دوباره بررسی کنید.",
  "setup.actions": "اقدام‌های انتشار",
  "readiness.otherActions": "عملیات دیگر آمادگی",
  "setup.progress": "{completed} بررسی از {total} مورد تأیید شده",
  "setup.progressLabel": "پیشرفت آماده‌سازی انتشار",
  "setup.manualOptional": "ویرایش دستی سیاست اختیاری است. آماده‌سازی می‌تواند از سیاست ذخیره‌شده یا پیش‌فرض‌های اعلام‌شدهٔ راه‌اندازی نخست استفاده کند.",
  "detail.publication": "وضعیت انتشار",
  "detail.moreActions": "عملیات نسخه",
  "detail.recordDetails": "جزئیات نسخه",
  "readiness.nextStep": "از مسیر فعلی راه‌اندازی یا به‌روزرسانی دستگاه ادامه دهید. تنظیمات انتشار و توزیع تغییری نکرده‌اند.",
  "readiness.issue.tufHealthInline": "اعتماد به‌روزرسانی قابل تمدید خودکار نیست. بررسی‌های همین‌جا را مرور کنید؛ بازبینی امنیتی لازم همچنان الزامی است.",
  "readiness.issue.tufRootInline": "مرجع Root قابل استفاده‌ای برای به‌روزرسانی در دسترس نیست. بررسی‌های آماده‌سازی همین‌جا را مرور کنید؛ مرجع موجود حفظ می‌شود.",
  "readiness.issue.tufSigningInline": "مرجع امضای مدیریت‌شده ناقص یا در دسترس نیست. بررسی‌های آماده‌سازی همین‌جا را مرور کنید؛ ممکن است بازبینی جداگانه لازم باشد.",
  "readiness.issue.tufConfigurationInline": "پیکربندی لازم برای اعتماد به‌روزرسانی در این نصب کامل نیست. خطای زیر را بررسی کنید؛ ممکن است اصلاح تنظیمات نصب لازم باشد.",
  "readiness.issue.tufPolicyInline": "سیاست نگهداری ذخیره نشده است. آماده‌سازی تأییدشده می‌تواند از پیش‌فرض‌های اعلام‌شدهٔ راه‌اندازی نخست استفاده کند؛ ویرایش دستی اختیاری است.",

  "setup.pendingRelease": "در انتظار تکمیل آمادگی به‌روزرسانی",
  "setup.pause": "توقف موقت راه‌اندازی",
  "setup.pausedBody": "پس از بررسی جاری متوقف می‌شود. کارهای انجام‌شده ذخیره‌اند؛ پیش از ادامه دوباره بررسی کنید.",
  "setup.title": "انتشار امن کالکتور",
  "setup.scope": "بتای ویندوز · راه‌اندازی یک‌باره",
  "setup.body": "بررسی‌ها را مرور و تأیید کنید تا انتشار همین‌جا آماده و راستی‌آزمایی شود.",
  "setup.signer": "مرجع امضای کالکتور",
  "setup.policy": "سیاست اعتبار اعتماد به‌روزرسانی",
  "setup.keys": "کلیدهای امضای به‌روزرسانی",
  "setup.root": "زنجیرهٔ اعتماد عمومی تأییدشده",
  "setup.activation": "مرجع امضا فعال و بررسی شده",
  "setup.verified": "تأییدشده",
  "setup.pending": "آماده خواهد شد",
  "setup.loading": "در حال بررسی اعتماد موجود و آمادگی سرور…",
  "setup.working": "در حال آماده‌سازی و اعتبارسنجی انتشار امن…",
  "setup.approve": "تأیید راه‌اندازی امن",
  "setup.approval": "تغییرات به تأیید شما و احراز هویت چندعاملی اخیر نیاز دارند.",
  "setup.controls": "سیاست و جزئیات پیشرفته",
  "setup.policyBody": "سیاست موجود حفظ می‌شود. پیش‌فرض اولیه: Root برابر ۳۶۵ روز، Targets برابر ۹۰، Snapshot برابر ۳۰، Timestamp برابر ۱۴ و هشدار ۷ روز. تمدید همچنان عملیاتی مدیریت‌شده است.",
  "setup.preserve": "کلیدهای موجود دوباره استفاده می‌شوند. جایگزینی Root و ناسازگاری مرجع اعتماد بررسی جداگانه می‌خواهند. هر تغییر در Audit log ثبت می‌شود.",
  "setup.ready": "انتشار امن آماده است",
  "setup.complete": "عملیات تأییدشده کامل و اعتبارسنجی شد.",
  "setup.paused": "به بررسی شما نیاز است",
  "setup.retryBody": "این بررسی کامل نشد. مراحل انجام‌شده ذخیره شده‌اند. پیش از ادامه دوباره بررسی کنید؛ آمادگی نسخه برای به‌روزرسانی هنوز تأیید نشده است.",
  "setup.configuration": "پیکربندی لازم انتشار روی این سرور کامل نیست. کلید جایگزین ساخته نمی‌شود. پیکربندی استقرار باید اصلاح شود.",
  "setup.changed": "وضعیت بررسی‌شده تغییر کرده است. برنامهٔ فعلی را دوباره بررسی و تأیید کنید.",
  "setup.otherOperation": "راه‌اندازی دیگری در حال اجراست. برای دیدن نتیجه دوباره بررسی کنید.",
  "setup.reviewRequired": "اعتماد موجود به بررسی نیاز دارد. کاووش کلیدهای فعلی را حفظ کرده و خودکار جایگزین نمی‌کند. کد تشخیص در جزئیات فنی موجود است.",
  "setup.finish": "تکمیل آمادگی به‌روزرسانی",
  "setup.existing": "نسخهٔ موجود؛ ثبت، انتشار و دامنهٔ توزیع حفظ می‌شوند.",
  "setup.publish": "تأیید راه‌اندازی و انتشار بتا",
  "setup.intakeScope": "این بسته · بتای ویندوز · همهٔ دستگاه‌های مجاز بتا",
  "setup.registrationNote": "ثبت، این بسته را منتشر نمی‌کند. انتشار امن را می‌توانید در همین ویزارد آماده کنید.",
  "hub.eyebrow": "تحویل پلتفرم",
  "hub.title": "نسخه‌های Collector",
  "hub.description": "artifactهای Collector را اضافه، تأیید، ثبت، منتشر و مدیریت کنید.",
  "tab.registry": "نسخه‌های Collector",
  "tab.add": "افزودن Collector",
  "tab.consent": "Consent",
  "tab.consents": "Consentها",
  "workspace.aria": "فضای مدیریت Collector Release",
  "registry.aria": "فهرست Releaseهای Collector",
  "registry.eyebrow": "فهرست RELEASEهای COLLECTOR",
  "registry.title": "نسخه‌های Collector",
  "registry.description": "تمام authorityهای فعلی Collector، از جمله buildهای Portable منتشرشده در LAB را ببینید. Release منتشرشده به‌جای حذف پنهانی Withdraw می‌شود تا تاریخچه قابل ممیزی باقی بماند.",
  "action.refresh": "تازه‌سازی",
  "action.refreshing": "در حال تازه‌سازی…",
  "action.add": "افزودن Collector",
  "search.aria": "جستجوی Releaseهای Collector",
  "search.placeholder": "جستجو بر اساس نسخه، پلتفرم، channel یا hash…",
  "filter.sourceAria": "فیلتر منبع Collector Release",
  "filter.lifecycleAria": "فیلتر چرخه عمر Collector Release",
  "filter.sourceAll": "همه منابع",
  "filter.labPortable": "Portable · LAB",
  "filter.signedManifest": "Manifest امضاشده",
  "filter.signedBundle": "Bundle امضاشده",
  "filter.directArtifact": "Artifact مستقیم",
  "filter.stateAll": "همه وضعیت‌ها",
  "state.published": "منتشرشده",
  "state.registered": "ثبت‌شده",
  "state.verified": "تأییدشده",
  "state.revoked": "باطل‌شده",
  "state.unknown": "وضعیت دیگر",
  "readiness.ready": "آماده",
  "readiness.attention": "نیازمند توجه",
  "readiness.blocked": "مسدود",
  "readiness.readyBody": "بررسی‌های نسخه و اعتماد لازم برای به‌روزرسانی آن تأیید شده‌اند.",
  "readiness.updateTrust": "آمادگی به‌روزرسانی",
  "readiness.tufExact": "TUF همین بایت‌های کالکتور را مجاز می‌داند.",
  "readiness.tufNotExact": "TUF هنوز این بایت‌های کالکتور را مجاز نمی‌داند.",
  "readiness.repair": "آماده‌سازی امن برای Update",
  "readiness.repairing": "در حال اصلاح…",
  "readiness.recheck": "بررسی دوباره آمادگی",
  "readiness.renewTrust": "تمدید اعتماد Update",
  "readiness.renewingTrust": "در حال تمدید اعتماد…",
  "readiness.renewedReady": "اعتماد Update در نسل متادیتای v{version} تمدید شد و این Collector آماده است.",
  "readiness.renewedRepairNext": "اعتماد Update در نسل متادیتای v{version} تمدید شد. اعتماد دوباره سالم است؛ اکنون «آماده‌سازی امن برای Update» را اجرا کنید تا همین byteهای دقیق Collector مجاز شوند.",
  "readiness.renewedRecheck": "اعتماد Update در نسل متادیتای v{version} تمدید شد. پیش از ادامه، authority باقی‌مانده Release را دوباره بررسی کنید.",
  "readiness.renewError": "تمدید امن اعتماد Update ممکن نشد. ابتدا authority گزارش‌شده را بررسی و رفع کنید و فقط بعد دوباره تلاش کنید.",
  "readiness.managePolicy": "تنظیم سیاست اعتماد به‌روزرسانی",
  "readiness.policyRequired": "پیش از تمدید، سیاست نگهداری اعتماد به‌روزرسانی را همین‌جا تنظیم کنید. هیچ متادیتای اعتماد تغییر نکرد.",
  "readiness.openKeysTrust": "باز کردن Keys & Trust",
  "readiness.openConsent": "باز کردن Consent",
  "readiness.addSuccessor": "افزودن Collector بعدی",
  "readiness.repaired": "آمادگی Collector Release با authorityهای لازم هم‌راستا شد.",
  "readiness.recheckRequired": "Kavoosh اصلاح امن را انجام داد. پیش از ادامه آمادگی را دوباره بررسی کنید.",
  "readiness.error": "وضعیت آمادگی Collector Release قابل بررسی یا اصلاح نبود.",
  "readiness.issue.releaseNotPublished": "این Collector هنوز منتشر نشده است. پیش از استفاده برای Update، انتشار Release را کامل کنید.",
  "readiness.issue.artifactIncomplete": "اتصال immutable artifact این Release منتشرشده کامل نیست و نباید درجا بازنویسی شود. این Release را Withdraw کنید، سپس یک نسخه جدید و استفاده‌نشده از Collector را با artifact نهایی دقیق Add کنید؛ این هویت Release را دوباره استفاده نکنید.",
  "readiness.issue.consentIncomplete": "این Release منتشرشده فاقد Consent Set دقیق و governed است و نباید بازنویسی شود. سیاست Consent فعلی را کامل کنید و سپس یک نسخه جدید Collector را Add کرده و همان Consent Set دقیق را به آن bind کنید.",
  "readiness.issue.tufMismatch": "این Collector منتشر شده اما Windows update trust هنوز به byteهای دیگری اشاره می‌کند. Kavoosh می‌تواند TUF را به‌صورت امن روی همین Release دقیق به‌روزرسانی کند.",
  "readiness.issue.tufRecovery": "یک عملیات قبلی update trust برای همین Release در وضعیت قابل بازیابی متوقف شده است. Kavoosh می‌تواند آن را امن reconcile کند.",
  "readiness.issue.tufRoot": "Windows update trust فاقد Root authority قابل استفاده است. Keys & Trust را باز کنید و TUF Root لازم را بازیابی یا فعال کنید.",
  "readiness.issue.tufRootExpired": "مرجع TUF Root منقضی شده است. تمدید متادیتای mutable نمی‌تواند Root را تعمیر کند؛ Keys & Trust را باز کنید و مسیر بازیابی یا rotation مربوط به Root را انجام دهید.",
  "readiness.issue.tufExpired": "متادیتای Windows update trust منقضی شده است. همین‌جا آن را تمدید کنید؛ Kavoosh target دقیق Collector را بدون تغییر حفظ می‌کند، Root و signerهای مدیریت‌شده را بررسی می‌کند و Timestamp را در آخر ثبت می‌کند.",
  "readiness.issue.tufExpiredUnbound": "اعتماد به‌روزرسانی Windows پیش از مجازشدن هر target از Collector منقضی شده است. Kavoosh می‌تواند همین Release دقیق را هنگام ایجاد نسل تازه متادیتا به‌صورت امن متصل کند؛ Root و signerهای مدیریت‌شده دوباره بررسی می‌شوند و Timestamp در پایان ثبت می‌شود.",
  "readiness.issue.tufPolicy": "سیاست نگهداری اعتماد به‌روزرسانی تنظیم نشده است. آن را همین‌جا تنظیم کنید و سپس readiness این release را دوباره بررسی کنید.",
  "readiness.issue.tufHealth": "Windows update trust به دلیلی ناسالم است که تمدید خودکار آن امن نیست. Keys & Trust را باز کنید و مسیر اصلاح گزارش‌شده را دنبال کنید.",
  "readiness.issue.tufSigning": "authority امضای مدیریت‌شده TUF ناقص یا در دسترس نیست. signer مشخص‌شده در Keys & Trust را اصلاح و سپس دوباره بررسی کنید.",
  "readiness.issue.tufConfiguration": "Managed Windows update trust برای این deployment کامل پیکربندی نشده است. Keys & Trust را باز کنید، تنظیمات TUF نمایش‌داده‌شده را کامل کنید و سپس این Release را دوباره بررسی کنید.",
  "readiness.issue.tufRecoveryBlocked": "وضعیت بازیابی TUF نامعتبر است و اصلاح خودکار امن نیست. Keys & Trust را باز کرده و مسیر recovery را دنبال کنید.",
  "readiness.issue.tufRecoveryOther": "وضعیت بازیابی TUF متعلق به Release دیگری است. پیش از انتشار target جدید، آن recovery را در Keys & Trust حل کنید.",
  "readiness.issue.releaseIncompatible": "این Release با مسیر فعلی managed update trust سازگار نیست. پیش از ادامه release authority را بررسی کنید.",
  "readiness.issue.generic": "Kavoosh یک ناسازگاری authority تشخیص داد. آمادگی را دوباره بررسی کنید یا مسیر اصلاح پیشنهادی را دنبال کنید.",
  "history.show": "نمایش تاریخچه رکوردها",
  "metrics.aria": "شاخص‌های Collector Release",
  "intakes.aria": "Artifact intakeهای باز Collector",
  "intakes.eyebrow": "در حال تکمیل",
  "intakes.title": "Artifact intakeهای باز",
  "intakes.count": "{count} مورد باز",
  "intakes.identityPending": "هویت هنوز تعیین نشده",
  "intakes.unknownPlatform": "پلتفرم نامشخص",
  "intakes.consentBound": "Consent متصل است",
  "intakes.consentPending": "Consent در انتظار است",
  "intakes.state.stagingPending": "Staging در انتظار تکمیل است",
  "intakes.state.notStarted": "شروع نشده",
  "intakes.state.nativePending": "Native verification در انتظار است",
  "intakes.state.nativeVerified": "Native verification تأیید شد",
  "intakes.state.labPortableVerified": "Portable LAB تأیید شد",
  "intakes.state.failed": "ناموفق",
  "intakes.state.discardPending": "Discard در انتظار تکمیل است",
  "intakes.state.discarded": "Discard شده",
  "intakes.openWorkflow": "باز کردن workflow",
  "intakes.discard": "Discard",
  "intakes.discardBlocked": "پیش از Discard شدن intake، منتظر پایان native verification بمانید.",
  "empty.title": "Collector Release مطابق این نما وجود ندارد",
  "empty.body": "یک Collector اضافه کنید یا فیلترها را پاک کنید. Releaseهای Portable منتشرشده در LAB نیز در این فهرست دیده می‌شوند.",
  "directory.eyebrow": "فهرست RELEASE",
  "directory.title": "فهرست Releaseها",
  "directory.history": "تاریخچه رکورد Release",
  "directory.current": "Releaseهای فعلی",
  "directory.count": "{count} رکورد",
  "action.manage": "مدیریت",
  "detail.aria": "جزئیات Collector Release",
  "detail.eyebrow": "نسخهٔ کالکتور",
  "detail.close": "بستن",
  "detail.overview": "نمای کلی",
  "detail.overviewHelp": "وضعیت authoritative چرخه عمر و تحویل Release.",
  "detail.trust": "اعتماد و فایل",
  "detail.trustHelp": "بسته، runtime، manifest و authority امضای دقیق.",
  "detail.consentLifecycle": "رضایت و چرخهٔ عمر",
  "detail.consentLifecycleHelp": "Consent تغییرناپذیر و عملیات ممیزی‌شده Release.",
  "detail.refreshHelp": "بارگذاری دوباره وضعیت Registry تحت authority سرور.",
  "field.state": "وضعیت",
  "field.record": "رکورد",
  "field.packageSha": "SHA-256 بسته",
  "field.runtimeSha": "SHA-256 runtime",
  "field.artifactSize": "حجم فایل",
  "field.channel": "کانال انتشار",
  "field.rollout": "درصد توزیع",
  "field.distribution": "نوع توزیع",
  "field.platformOrigin": "Platform Origin",
  "field.manifestSha": "SHA-256 Manifest",
  "field.signingKey": "کلید امضا",
  "field.nativeVerifier": "Native verifier",
  "field.consentSet": "Consent Set",
  "field.artifactUrl": "URL artifact",
  "field.created": "ایجادشده",
  "field.published": "منتشرشده",
  "field.revoked": "باطل‌شده",
  "field.notPublished": "منتشر نشده",
  "field.openArtifact": "باز کردن artifact دقیق",
  "consent.title": "Consent authority",
  "consent.body": "Consent Set نگه‌داری‌شده توسط این Release، Evidence تاریخی و تغییرناپذیر است. نسخه‌های جدید Consent جداگانه مدیریت می‌شوند و برای درخواست‌های آینده اعمال می‌شوند، بدون اینکه رکورد این Release بازنویسی شود.",
  "consent.revise": "مدیریت نسخه‌های Consent",
  "consent.libraryTitle": "Consentهای متصل به این Release",
  "consent.libraryHelp": "این‌ها همان سندهای Consent تاریخی هستند که دقیقاً با این Collector Release نگه‌داری شده‌اند. تاریخچه متصل فقط خواندنی است؛ ساخت، ترجمه، انتشار یا بازیابی نسخه‌های آینده از بخش سیاست‌های Consent انجام می‌شود.",
  "consent.librarySearch": "جستجوی Consent بر اساس پروفایل، زبان یا نسخه",
  "consent.libraryLanguage": "زبان",
  "consent.libraryAllLanguages": "همه زبان‌ها",
  "consent.libraryProfile": "پروفایل جمع‌آوری",
  "consent.libraryVersion": "نسخه متصل",
  "consent.libraryRecord": "رکورد محتوا",
  "consent.libraryPreview": "پیش‌نمایش نسخه متصل",
  "consent.libraryAction": "مدیریت نسخه فعلی",
  "consent.libraryPreviewUnavailable": "این سند Consent تاریخی دیگر برای پیش‌نمایش دقیق در دسترس نیست.",
  "consent.libraryCount": "{count} سند",
  "consent.libraryEmpty": "این Release جزئیات قابل جستجوی سندهای Consent را نگه نمی‌دارد. Digest تاریخی Consent Set همچنان authority معتبر است.",
  "consent.libraryNoMatch": "هیچ Consent متصلی با این جستجو پیدا نشد.",
  "consent.libraryImmutable": "Consent تاریخی متصل به Release قابل حذف یا ویرایش نیست. مدیریت Draft و نسخه‌های آینده در بخش سیاست‌های Consent انجام می‌شود.",
  "action.changeRollout": "تغییر Rollout",
  "action.withdraw": "لغو انتشار نسخه",
  "lab.rolloutNote": "در قرارداد فعلی، Rollout نسخه Portable LAB با مقدار ۱۰۰٪ امضا شده و درجا قابل تغییر نیست. Withdraw ممکن است؛ Rollout متغیر باید authority جداگانه داشته باشد، نه اینکه Manifest امضاشده تغییر کند.",
  "discard.aria": "Discard کردن Collector artifact intake",
  "discard.eyebrow": "DISCARD INTAKE",
  "discard.body": "این کار artifact نهایی‌نشده را از کار فعال Admin خارج می‌کند و فقط وقتی مجاز است که هیچ Collector Release authoritative به این intake ارجاع ندهد. Release ثبت یا منتشرشده اینجا هرگز hard-delete نمی‌شود.",
  "discard.cancel": "لغو",
  "discard.confirm": "Discard intake",
  "discard.working": "در حال Discard…",
  "withdraw.aria": "Withdraw کردن Collector Release",
  "withdraw.eyebrow": "WITHDRAW RELEASE",
  "withdraw.body": "Withdraw یک رکورد revoked قابل ممیزی می‌سازد و این Release را از انتخاب‌های bootstrap آینده خارج می‌کند. Evidence تاریخی Release حفظ می‌شود.",
  "withdraw.reason": "دلیل",
  "withdraw.placeholder": "توضیح دهید چرا این Release نباید دیگر ارائه شود.",
  "withdraw.cancel": "لغو",
  "withdraw.confirm": "Withdraw release",
  "withdraw.working": "در حال اعمال…",
  "rollout.aria": "تغییر Rollout",
  "rollout.eyebrow": "کنترل ROLLOUT",
  "rollout.body": "درصد Deviceهای واجد شرایط برای دریافت این Release منتشرشده را تغییر دهید.",
  "rollout.label": "درصد Rollout",
  "rollout.cancel": "لغو",
  "rollout.confirm": "اعمال Rollout",
  "rollout.working": "در حال اعمال…",
  "status.withdrawn": "{version} Withdraw شد و دیگر در bootstrap جدید انتخاب نمی‌شود.",
  "status.rollout": "Rollout نسخه {version} اکنون {percent}٪ است.",
  "status.discarded": "{filename} Discard شد. هیچ Release authority حذف نشد.",
  "error.load": "Collector Releaseها بارگذاری نشدند.",
  "error.change": "تغییر Collector Release انجام نشد.",
  "error.discard": "Artifact intake قابل Discard نبود.",
  "source.labPortable": "Portable · LAB",
  "source.directArtifact": "Artifact مستقیم",
  "source.signedBundle": "Bundle امضاشده",
  "source.signedManifest": "Manifest امضاشده",
  "distribution.portable": "Portable",
  "distribution.setup": "Setup",
  "channel.beta": "Beta",
  "channel.stable": "Stable",
  "metrics.current": "Releaseهای فعلی",
  "metrics.currentHelp": "آخرین authority برای هر release key",
  "metrics.published": "منتشرشده",
  "metrics.publishedHelp": "رکوردهای Release فعال و واجد شرایط",
  "metrics.intakes": "Intakeهای باز",
  "metrics.intakesHelp": "Artifactهای پیش‌نویس که هنوز به Release متصل نشده‌اند",
  "filter.sourceLabel": "منبع",
  "filter.stateLabel": "وضعیت",
  "filter.clear": "پاک کردن فیلترها",
  "column.collector": "Collector",
  "column.platform": "پلتفرم",
  "column.distribution": "نوع توزیع",
  "column.channel": "Channel",
  "column.state": "وضعیت",
  "column.updated": "به‌روزرسانی",
  "column.artifact": "Artifact",
  "column.version": "نسخه",
  "column.verification": "تأیید",
  "column.consent": "Consent",
  "column.actions": "عملیات",
  "directory.rollout": "Rollout {percent}٪",
  "directory.auditNote": "سوابق منتشرشده پس از لغو انتشار نیز برای ممیزی حفظ می‌شوند.",
  "intakes.separation": "Artifactهای منتشرنشده جدا از فهرست authoritative Release نگه داشته می‌شوند.",
  "consentUX.modeTabsAria": "حالت‌های Consent مربوط به Collector",
  "intakes.state.publisherVerified": "امضای ناشر تأیید شده",
  "field.publisherSignature": "اثر انگشت ناشر معتبر",
  "add.verification.publisherVerify": "بررسی امضای ناشر ZIP",
  "add.verification.publisherPassed": "امضای ناشر به‌صورت مستقل تأیید شد.",
  "add.verification.publisherNotNative": "بررسی امضای ناشر مستقل از اعتبارسنجی بومی امضای کد ویندوز است.",
  "add.error.publisherVerificationFailed": "بررسی ناشر ناموفق بود. کلید اعتماد و ZIP امضاشده را بررسی کنید.",
  "add.consent.missingRequired": "پیش از ذخیره، متغیرهای الزامی جمع‌آوری را اضافه کنید: {variables}",
  "consentUX.identityReady": "هویت تأیید شد.",
  "consentUX.sharedEditor": "Consent توسط ادمین در Collector Releases نوشته می‌شود و از پیش‌نویس مدیریت‌شده، preview دقیق و gate انتشار استفاده می‌کند.",
  "consentUX.published": "منتشرشده",
  "consentUX.draft": "پیش‌نویس",
  "consentUX.notPublished": "منتشر نشده",
  "consentUX.eyebrow": "COLLECTOR CONSENT",
  "consentUX.fallback": "Consent مدیریت‌شده {mode} برای {platform}.",
  "consentUX.validatedDraft": "این پیش‌نویس دقیق از preview و validation انتشار عبور کرده است.",
  "consentUX.reused": "نسخه منتشرشده فعلی Collector Consent دوباره استفاده می‌شود.",
  "consentUX.immutableDraft": "پیش‌نویس immutable ایجاد شد",
  "consentUX.previewInstruction": "پیش از انتشار، Consent دقیق رندرشده توسط سرور را بازبینی کنید.",
  "consentUX.loading": "در حال بارگذاری…",
  "consentUX.previewExact": "Preview دقیق Consent",
  "consentUX.exactPreview": "Preview دقیق",
  "consentUX.publishing": "در حال انتشار…",
  "consentUX.publishExact": "انتشار نسخه دقیق",
  "consentUX.authorityTitle": "Authority مربوط به Windows Collector",
  "consentUX.profile": "Profile",
  "consentUX.generation": "Generation",
  "consentUX.authoritySha": "SHA-256 Authority",
  "consentUX.artifactScopeSha": "SHA-256 دامنه Artifact",
  "consentUX.boundSha": "SHA-256 بدنه متصل",
  "consentUX.bindHelp": "فقط وقتی هر سه Rapid Triage، Deep Forensics و Advanced Threat Hunt نسخه دقیق Consent منتشرشده دارند، آن‌ها را Bind کنید.",
  "consentManager.aria": "مدیریت Collector Consent",
  "consentManager.eyebrow": "COLLECTOR CONSENT",
  "consentManager.title": "مدیریت Consent",
  "consentManager.description": "Consent پیش از جمع‌آوری فارنزیک را بنویسید، نسخه‌بندی، ترجمه، preview، منتشر و بازیابی کنید. انگلیسی زبان منبع authoritative است.",
  "consentManager.platform": "پلتفرم",
  "consentManager.profile": "پروفایل جمع‌آوری",
  "consentManager.language": "زبان",
  "consentManager.englishDefault": "انگلیسی منبع داخلی و پیش‌فرض است. زبان‌های دیگر ترجمه‌های مدیریت‌شده و متصل به یک نسخه دقیق انگلیسی هستند.",
  "consentManager.current": "نسخه فعلی",
  "consentManager.notConfigured": "پیکربندی نشده",
  "consentManager.newVersion": "ایجاد نسخه جدید",
  "consentManager.editTranslation": "ویرایش ترجمه",
  "consentManager.preview": "Preview",
  "consentManager.publish": "انتشار",
  "consentManager.effectiveAt": "تاریخ و زمان اعمال",
  "consentManager.effectiveAtHelp": "برای انتشار فوری خالی بگذارید. برای Cutover برنامه‌ریزی‌شده یک زمان آینده انتخاب کنید.",
  "consentManager.effectiveAtInvalid": "یک تاریخ و زمان معتبر برای اعمال انتخاب کنید.",
  "consentManager.sendReview": "ارسال برای بازبینی",
  "consentManager.markReady": "آماده انتشار",
  "consentManager.history": "تاریخچه نسخه‌ها",
  "consentManager.rollback": "بازیابی به‌عنوان نسخه جدید",
  "consentManager.refresh": "تازه‌سازی",
  "consentManager.sourceRequired": "پیش از ساخت ترجمه، منبع انگلیسی را منتشر کنید.",
  "consentManager.translationSource": "منبع ترجمه",
  "consentManager.translationDraft": "پیش‌نویس ترجمه",
  "consentManager.translationTitle": "عنوان ترجمه‌شده",
  "consentManager.translationBody": "متن ترجمه‌شده",
  "consentManager.changeSummary": "خلاصه تغییر",
  "consentManager.saveTranslation": "ذخیره پیش‌نویس ترجمه",
  "consentManager.cancel": "لغو",
  "consentManager.readyHelp": "پیش از انتشار، ترجمه Ready را دقیق Preview کنید.",
  "consentManager.published": "منتشرشده",
  "consentManager.draft": "پیش‌نویس",
  "consentManager.inReview": "در حال بازبینی",
  "consentManager.ready": "آماده",
  "consentManager.outOfDate": "نیازمند به‌روزرسانی",
  "consentManager.missing": "موجود نیست",
  "consentManager.saved": "پیش‌نویس Consent ذخیره شد.",
  "consentManager.translationSaved": "پیش‌نویس ترجمه ذخیره شد.",
  "consentManager.publishedNotice": "نسخه Consent منتشر شد.",
  "consentManager.rolledBack": "Consent تاریخی به‌عنوان یک نسخه منتشرشده جدید بازیابی شد.",
  "consentManager.error": "Collector Consent به‌روزرسانی نشد.",
  "consentManager.directoryTitle": "سیاست‌های Consent",
  "consentManager.directoryDescription": "پلتفرم Collector و پروفایل جمع‌آوری را انتخاب کنید. هر سیاست نسخه فعلی، پوشش زبان، تاریخچه و اتصال‌های Release خودش را دارد.",
  "consentManager.platformAria": "پلتفرم Collector",
  "consentManager.manageProfile": "مدیریت Consent",
  "consentManager.currentPublished": "نسخه منتشرشده فعلی",
  "consentManager.noPublishedVersion": "نسخه منتشرشده ندارد",
  "consentManager.versionCount": "{count} نسخه",
  "consentManager.languageCoverage": "پوشش زبان",
  "consentManager.languageCoverageValue": "{current} از {total} به‌روز",
  "consentManager.languageAttention": "{count} مورد نیازمند توجه",
  "consentManager.latestCollector": "آخرین Collector منتشرشده",
  "consentManager.noPublishedCollector": "Collector منتشرشده ندارد",
  "consentManager.releaseBindings": "اتصال‌های Release",
  "consentManager.releaseBindingCount": "{count} Release",
  "consentManager.backToOverview": "همه سیاست‌های Consent",
  "consentManager.currentForRequests": "برای درخواست‌های جدید {profile} استفاده می‌شود",
  "consentManager.languagesTitle": "زبان‌ها",
  "consentManager.languagesHelp": "انگلیسی منبع است. هر ترجمه به یک نسخه دقیق انگلیسی متصل است و با تغییر منبع باید به‌روزرسانی شود.",
  "consentManager.sourceLanguage": "منبع",
  "consentManager.chooseLanguage": "مدیریت این زبان",
  "consentManager.boundToSource": "متصل به EN {version}",
  "consentManager.releaseBindingsTitle": "اتصال‌های Collector Release",
  "consentManager.releaseBindingsDescription": "هر Release همان Consent Set دقیقی را که هنگام ثبت یا انتشار گرفته حفظ می‌کند. نسخه جدید Consent این اتصال تاریخی را بازنویسی نمی‌کند.",
  "consentManager.bindingLoadError": "جزئیات اتصال Collector Release بارگذاری نشد. مدیریت Consent همچنان در دسترس است.",
  "consentManager.bindingExact": "Consent متصل {version} · {count} زبان",
  "consentManager.bindingDigestOnly": "جزئیات دقیق این پروفایل در Consent Set حفظ شده اما از این ردیف Release قابل بازیابی نیست.",
  "consentManager.currentState": "فعلی",
  "consentManager.previousState": "قبلاً منتشرشده",
  "consentManager.notBound": "اتصال Release ندارد",
  "consentManager.releaseBindingState": "{state} · Consent Set {digest}",
  "intakes.emptyTitle": "Artifact intake بازی وجود ندارد",
  "intakes.emptyBody": "workflow ناتمام Collector artifact وجود ندارد. هر زمان برای ثبت artifact جدید آماده بودید Add Collector را شروع کنید.",
  "add.mode.metadata": "Rapid Triage",
  "add.mode.extended": "Deep Forensics",
  "add.mode.memory": "Advanced Threat Hunt",
  "add.stage.artifact": "Artifact",
  "add.stage.identity": "هویت",
  "add.stage.consent": "Consent",
  "add.stage.verification": "Verification",
  "add.stage.review": "بازبینی",
  "add.stage.done": "انجام‌شده",
  "add.error.selectedNotResumable": "Collector intake انتخاب‌شده قابل ادامه نیست.",
  "add.error.selectedResumeFailed": "Collector intake انتخاب‌شده ادامه داده نشد.",
  "add.error.noResumable": "Collector intake قابل ادامه‌ای موجود نیست.",
  "add.error.resumeFailed": "Collector intake ادامه داده نشد.",
  "add.error.uploadFailed": "بارگذاری artifact ناموفق بود.",
  "add.error.identityRequired": "سیستم‌عامل، معماری، نسخه و host OS الزامی باید مشخص شوند.",
  "add.error.identityFailed": "تأیید هویت ناموفق بود.",
  "add.error.localeUnsupported": "پیش از ذخیره این Consent draft یک locale آماده و پشتیبانی‌شده انتخاب کنید.",
  "add.error.consentDraftFailed": "ساخت Consent draft ناموفق بود.",
  "add.error.consentPreviewFailed": "پیش‌نمایش Consent ناموفق بود.",
  "add.error.contentValidationFailed": "اعتبارسنجی محتوا این draft را تأیید نکرد.",
  "add.error.consentPublicationFailed": "انتشار Consent ناموفق بود.",
  "add.error.allConsentsRequired": "پیش از binding، هر سه نسخه دقیق Consent را منتشر کنید.",
  "add.error.consentBindingFailed": "اتصال Consent ناموفق بود.",
  "add.error.labVerificationFailed": "تأیید integrity مربوط به LAB portable ناموفق بود.",
  "add.error.labPublicationFailed": "انتشار LAB portable ناموفق بود.",
  "add.error.nativeVerificationStartFailed": "Native verification شروع نشد.",
  "add.error.verificationRefreshFailed": "وضعیت verification تازه‌سازی نشد.",
  "add.error.finalAddFailed": "Final Add ناموفق بود.",
  "add.consent.bindWorking": "در حال اتصال…",
  "add.consent.bindAction": "اتصال سه نسخه Consent",
  "add.verification.aria": "تأیید Collector",
  "add.verification.boundTitle": "Consent Set متصل شد",
  "add.verification.targetOs": "سیستم‌عامل هدف",
  "add.verification.state": "Verification",
  "add.verification.starting": "در حال شروع…",
  "add.verification.runNative": "اجرای native verification",
  "add.verification.checking": "در حال بررسی…",
  "add.verification.labTest": "تأیید به‌عنوان تست LAB portable",
  "add.verification.pending": "Windows WinVerifyTrust verification در انتظار است.",
  "add.verification.refreshing": "در حال تازه‌سازی…",
  "add.verification.refresh": "تازه‌سازی وضعیت verification",
  "add.verification.failed": "Native verification ناموفق بود: {reason}",
  "add.verification.labPassed": "Integrity مربوط به LAB portable تأیید شد.",
  "add.verification.verifier": "Verifier",
  "add.verification.package": "Package",
  "add.verification.runtimeSha": "Runtime SHA-256",
  "add.verification.runtimeSize": "اندازه Runtime",
  "add.verification.authenticode": "Authenticode",
  "add.verification.trustScope": "دامنه اعتماد",
  "add.verification.publishLab": "انتشار در LAB Beta",
  "add.verification.nativePassed": "Native verification موفق بود.",
  "add.verification.policy": "Policy",
  "add.verification.verified": "تأییدشده",
  "add.verification.reviewFinal": "بازبینی Final Add",
  "add.review.aria": "بازبینی Final Add",
  "add.review.title": "بازبینی authority دقیق Collector",
  "add.review.artifact": "Artifact",
  "add.review.artifactSize": "اندازه Artifact",
  "add.review.artifactSha": "Artifact SHA-256",
  "add.review.architecture": "معماری",
  "add.review.version": "نسخه",
  "add.review.consentSet": "Consent Set",
  "add.review.back": "بازگشت",
  "add.review.adding": "در حال افزودن…",
  "add.review.finalAdd": "Final Add Collector",
  "add.success.labAria": "LAB Collector منتشر شد",
  "add.success.labTitle": "LAB Collector منتشر شد",
  "add.success.releaseRecord": "رکورد Release",
  "add.success.manifestSha": "Manifest SHA-256",
  "add.success.signingKey": "کلید امضا",
  "add.success.channel": "Channel",
  "add.success.artifactUrl": "Artifact URL",
  "add.success.registeredAria": "Collector ثبت شد",
  "add.success.registeredTitle": "Collector ثبت شد",
  "add.success.reservation": "Reservation",
  "add.success.receiptSha": "Receipt SHA-256",
  "add.success.viewRegistered": "مشاهده Collectorهای ثبت‌شده",
  "add.success.done": "انجام‌شده",
  "add.intro.aria": "افزودن Collector",
  "add.intro.title": "افزودن Collector",
  "add.intro.body": "یک Collector artifact نهایی را بارگذاری، تأیید، بازبینی و ثبت کنید.",
  "add.workflow.aria": "workflow افزودن Collector",
  "add.workflow.eyebrow": "COLLECTOR ARTIFACT INTAKE",
  "add.workflow.title": "افزودن Collector",
  "add.workflow.body": "بسته را اضافه کنید، مشخصاتش را تأیید کنید و پیش از ثبت یا انتشار، نتیجه را بررسی کنید.",
  "add.workflow.progressAria": "پیشرفت افزودن Collector",
  "add.step1.eyebrow": "مرحله ۲ از ۷",
  "add.step1.title": "Collector artifact",
  "add.step1.body": "یک executable، installer یا ZIP package نهایی Collector بارگذاری کنید.",
  "add.step1.choose": "انتخاب artifact یا ZIP",
  "add.step1.types": "EXE، MSI، package یا ZIP",
  "add.step1.authorityHelp": "byteهای انتخاب‌شده hash می‌شوند و authority artifact این intake خواهند بود.",
  "add.step1.loading": "در حال بارگذاری…",
  "add.step1.resume": "ادامه آخرین intake",
  "add.step1.uploading": "در حال بارگذاری…",
  "add.step1.upload": "بارگذاری artifact",
  "add.step2.eyebrow": "مرحله ۳ از ۷",
  "add.step2.title": "تأیید هویت",
  "add.step2.body": "پیشنهاد سرور را بازبینی و پیش از ایجاد authority پایدار هویت، در صورت نیاز اصلاح کنید.",
  "add.step2.filename": "نام فایل",
  "add.step2.size": "اندازه",
  "add.step2.sha": "SHA-256",
  "add.step2.suggestedOs": "سیستم‌عامل پیشنهادی",
  "add.step2.noSuggestion": "پیشنهاد امنی وجود ندارد",
  "add.step2.select": "انتخاب",
  "add.step2.help": "خروجی detector فقط پیشنهاد است. هویت تأییدشده توسط Admin authoritative می‌شود.",
  "add.step2.saving": "در حال ذخیره…",
  "add.step2.confirm": "تأیید هویت",
  "add.step3.eyebrow": "مرحله ۴ از ۷",
  "add.step3.title": "Consent",
  "add.step3.body": "برای هر collection mode یک نسخه دقیق و governed از Consent را منتشر و bind کنید.",
  "add.step4.eyebrow": "مرحله ۵ از ۷",
  "add.step4.title": "Native verification",
  "add.step4.body": "Kavoosh artifact دقیق را در برابر پلتفرم هدف تأییدشده verify می‌کند.",
  "add.step5.eyebrow": "مرحله ۶ از ۷",
  "add.step5.title": "بازبینی Collector",
  "add.step5.body": "authority دقیق و تغییرناپذیری را که ثبت خواهد شد تأیید کنید.",
  "add.action.open": "افزودن Collector",
  "add.action.cancel": "لغو",
  "add.preview.draft": "پیش‌نویس",
  "add.consent.previewAria": "پیش‌نمایش دقیق {mode}",
  "add.verification.consentSetSha": "Consent Set SHA-256",
  "add.verification.unsupported": "Adapter مربوط به native verification برای {platform} پیاده‌سازی نشده است. Final Add برای این هدف fail-closed باقی می‌ماند.",
  "add.verification.labWarning": "LAB portable verification فقط integrity مربوط به ZIP دقیق و Windows runtime را بررسی می‌کند. ادعای Authenticode trust ندارد و نمی‌تواند Production Final Add را باز کند.",
  "add.verification.labResultWarning": "این فقط یک نتیجه integrity مخصوص LAB است. Production Final Add قفل می‌ماند. انتشار LAB authority امضاشده جداگانه‌ای است.",
  "add.review.targetOs": "سیستم‌عامل هدف",
  "add.review.authorityHelp": "Final Add این component/version را برای همیشه reserve می‌کند و فقط byteهای artifact تأییدشده را promote می‌کند. Collector را publish یا rollout نمی‌کند.",
  "add.success.labBody": "ZIP portable دقیق تحت authority مدیریت‌شده release امضاشده در LAB Beta منتشر شد. Production Final Add بدون تغییر باقی می‌ماند.",
  "add.success.labNext": "گام بعد: wizard فارنزیک Client را برای یک دستگاه Windows باز کنید، این Release را resolve کنید، همان ZIP را دانلود و secure enrollment را شروع کنید.",
  "add.success.registeredBody": "Artifact دقیق تأییدشده ثبت شد. هیچ چیزی به‌صورت خودکار publish یا rollout نشد.",
  "add.field.operatingSystem": "سیستم‌عامل",
  "add.field.architecture": "معماری",
  "add.field.version": "نسخه",
  "add.field.component": "Component",
  "add.field.hostOperatingSystem": "سیستم‌عامل Host",
  "add.component.mobileHost": "Mobile host",
  "add.component.collector": "Collector",
  "add.stage.platform": "پلتفرم و نوع بسته",
  "add.platform.windows": "Windows",
  "add.platform.macos": "macOS",
  "add.platform.linux": "Linux",
  "add.platform.android": "Android",
  "add.platform.ios": "iOS",
  "add.stepPlatform.eyebrow": "مرحله ۱ از ۷",
  "add.stepPlatform.title": "پلتفرم و نوع بسته را انتخاب کنید",
  "add.stepPlatform.body": "خانواده Collector و بسته‌ای را که تیم Release Engineering تحویل داده انتخاب کنید.",
  "add.stepPlatform.help": "Kavoosh فقط capabilityهای پشتیبانی‌شده توسط سرور را نشان می‌دهد. هر نوع بسته جداگانه ثبت می‌شود.",
  "add.distribution.portableHelp": "بدون نصب اجرا می‌شود.",
  "add.distribution.setupHelp": "روی سیستم هدف یا Host نصب می‌شود.",
  "add.next": "بعدی",
  "add.back": "بازگشت",
  "add.error.foundationLoad": "Capabilityهای Collector Release یا تنظیمات زبان بارگذاری نشدند.",
  "add.error.androidConsentUnavailable": "سرور این محیط از Consent اندروید USB برای هر سه حالت پشتیبانی نمی‌کند. پیش از پیش‌نمایش یا انتشار رضایت‌نامه‌ها، پلتفرم را به‌روزرسانی کنید. پیش‌نویس‌ها و متغیر دامنهٔ Android را نگه دارید.",
  "add.error.hostRequired": "سیستم‌عامل Host این Mobile Host Collector را انتخاب کنید.",
  "add.consent.coverageReady": "همه زبان‌های فعال آماده‌اند",
  "add.consent.coverageNeedsAttention": "محتوای زبان‌ها نیازمند تکمیل است",
  "add.consent.languagesAria": "زبان‌های فعال Consent",
  "add.consent.activeLanguages": "{count} زبان فعال",
  "add.consent.languagesFromPlatform": "این فهرست مستقیماً از تنظیمات زبان Platform می‌آید و در این wizard زبان جدیدی اضافه نمی‌شود.",
  "add.consent.current": "فعلی",
  "add.consent.title": "عنوان",
  "add.consent.body": "متن Consent",
  "add.consent.titlePlaceholder": "یک عنوان روشن برای Consent وارد کنید",
  "add.consent.bodyPlaceholder": "متن Consent این زبان و پروفایل را بنویسید",
  "add.consent.variables": "متغیرهای در دسترس",
  "add.consent.variablesHelp": "این متغیرها از template مدیریت‌شده Consent همین پروفایل می‌آیند.",
  "add.consent.required": "الزامی",
  "add.consent.saveDraft": "ذخیره پیش‌نویس",
  "add.consent.allLanguagesReady": "هر سه پروفایل فارنزیک برای همه زبان‌های فعال به‌روز هستند.",
  "add.consent.completeAllLanguages": "پیش از ادامه، Consent همه زبان‌های فعال را کامل و منتشر کنید.",
  "add.consent.languages": "زبان‌های Consent",
  "add.verification.body": "Kavoosh دقیقاً همان byteهای بارگذاری‌شده را verify می‌کند. جزئیات فنی قابل مشاهده‌اند اما ورودی Admin نیستند.",
  "add.verification.labTools": "ابزارهای تست LAB",
  "add.technicalDetails": "جزئیات فنی",
  "add.success.readyForPublication": "ثبت شد و برای مرز انتشار آماده است",
  "add.success.publicationBody": "ثبت کامل شده است. انتشار و rollout یک اقدام کنترل‌شده جداگانه است و از ثبت نتیجه‌گیری نمی‌شود.",
};

export const COLLECTOR_RELEASE_REGISTRY_FA_MESSAGES = fa;

function resolve(locale: InstallableLocale, key: CollectorReleaseRegistryMessageKey): string {
  if (locale === "fa" && fa[key]) return fa[key]!;
  return en[key];
}

export function collectorReleaseRegistryMessage(
  locale: InstallableLocale,
  key: CollectorReleaseRegistryMessageKey,
  values?: MessageValues,
): string {
  return formatMessage(resolve(locale, key), values);
}

export function useCollectorReleaseRegistryCopy() {
  const { locale } = useWorkspaceCopy();
  const t = useCallback(
    (key: CollectorReleaseRegistryMessageKey, values?: MessageValues) =>
      collectorReleaseRegistryMessage(locale, key, values),
    [locale],
  );
  return { locale, t };
}
